/**
 * realtimePoolManager.js
 *
 * Implements connection pooling and debounced batch updates for Supabase
 * real-time channels to prevent performance degradation under high-frequency
 * changes in Mon Espace and admin panels.
 *
 * Key features:
 *  - Channel pooling: reuses a single Supabase channel per unique key instead
 *    of creating a new one on every component mount.
 *  - Debounced callbacks: coalesces rapid successive events into a single
 *    handler invocation after a configurable quiet period (default 300 ms).
 *  - Batch flushing: accumulates payloads during the debounce window and
 *    delivers them as an array so callers can decide how to process them.
 *  - Ref-counting: the underlying channel is only removed from Supabase when
 *    the last subscriber unsubscribes, preventing premature channel teardown.
 */

import { supabase } from '../lib/supabase';

// ─── Internal state ──────────────────────────────────────────────────────────

/**
 * Pool entry shape:
 * {
 *   channel: RealtimeChannel,
 *   subscribers: Map<subscriberId, { callback, debounceMs, timerId, batch }>,
 *   refCount: number,
 *   status: 'SUBSCRIBING' | 'SUBSCRIBED' | 'CLOSED',
 *   statusListeners: Set<fn>,
 * }
 */
const pool = new Map();

let _subscriberCounter = 0;
const nextSubscriberId = () => `sub_${++_subscriberCounter}_${Date.now()}`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Build a deterministic pool key from a channel name + postgres_changes filter.
 */
export const buildChannelKey = (channelName) => channelName;

/**
 * Flush the accumulated batch for a subscriber and invoke its callback.
 */
function flushBatch(entry, subscriberId) {
  const sub = entry?.subscribers?.get(subscriberId);
  if (!sub) return;

  const batch = [...(sub?.batch || [])];
  sub.batch = [];
  sub.timerId = null;

  if (batch?.length > 0) {
    try {
      sub?.callback(batch);
    } catch (err) {
      console.error('[realtimePoolManager] subscriber callback error:', err);
    }
  }
}

/**
 * Schedule (or reschedule) the debounce timer for a subscriber.
 */
function scheduleFlush(entry, subscriberId, payload) {
  const sub = entry?.subscribers?.get(subscriberId);
  if (!sub) return;

  // Accumulate payload into the batch
  sub.batch = sub?.batch || [];
  sub?.batch?.push(payload);

  // Clear any existing timer
  if (sub?.timerId !== null) {
    clearTimeout(sub?.timerId);
  }

  // Schedule flush after debounce window
  sub.timerId = setTimeout(() => {
    flushBatch(entry, subscriberId);
  }, sub?.debounceMs ?? 300);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Subscribe to a pooled real-time channel.
 *
 * @param {object} options
 * @param {string}   options.channelName   - Unique channel identifier (e.g. "admin-pending-applications")
 * @param {object[]} options.listeners     - Array of postgres_changes listener configs:
 *                                           { event, schema, table, filter? }
 * @param {function} options.callback      - Called with an array of accumulated payloads after debounce
 * @param {number}   [options.debounceMs=300] - Debounce window in milliseconds
 * @param {function} [options.onStatusChange] - Optional fn(status) called on channel status changes
 *
 * @returns {function} unsubscribe - Call this to release the subscription
 */
export function subscribePooled({
  channelName,
  listeners,
  callback,
  debounceMs = 300,
  onStatusChange,
}) {
  const key = buildChannelKey(channelName);
  const subscriberId = nextSubscriberId();

  let entry = pool?.get(key);

  if (!entry) {
    // Create a new pooled channel
    let channel = supabase?.channel(channelName);

    if (!channel) {
      console.warn('[realtimePoolManager] supabase.channel() returned null for', channelName);
      return () => {};
    }

    entry = {
      channel,
      subscribers: new Map(),
      refCount: 0,
      status: 'SUBSCRIBING',
      statusListeners: new Set(),
    };

    pool?.set(key, entry);

    // Register all postgres_changes listeners BEFORE subscribe()
    (listeners || [])?.forEach(({ event, schema, table, filter }) => {
      const config = { event, schema, table };
      if (filter) config.filter = filter;

      channel = channel?.on('postgres_changes', config, (payload) => {
        // Fan-out to all current subscribers of this pool entry
        const currentEntry = pool?.get(key);
        if (!currentEntry) return;
        currentEntry?.subscribers?.forEach((sub, sid) => {
          scheduleFlush(currentEntry, sid, payload);
        });
      });
    });

    // Subscribe and track status
    channel?.subscribe((status) => {
      const currentEntry = pool?.get(key);
      if (!currentEntry) return;
      currentEntry.status = status;
      currentEntry?.statusListeners?.forEach((fn) => {
        try { fn(status); } catch (_) {}
      });
    });

    // Update entry with the fully-chained channel reference
    entry.channel = channel;
    pool?.set(key, entry);
  }

  // Register this subscriber
  entry.refCount += 1;
  entry?.subscribers?.set(subscriberId, {
    callback,
    debounceMs,
    timerId: null,
    batch: [],
  });

  // Register status listener if provided
  if (typeof onStatusChange === 'function') {
    entry?.statusListeners?.add(onStatusChange);
    // Immediately notify with current status if already subscribed
    if (entry?.status === 'SUBSCRIBED') {
      try { onStatusChange('SUBSCRIBED'); } catch (_) {}
    }
  }

  // Return unsubscribe function
  return function unsubscribe() {
    const currentEntry = pool?.get(key);
    if (!currentEntry) return;

    const sub = currentEntry?.subscribers?.get(subscriberId);
    if (sub) {
      // Cancel any pending debounce timer
      if (sub?.timerId !== null) {
        clearTimeout(sub?.timerId);
      }
      currentEntry?.subscribers?.delete(subscriberId);
    }

    if (typeof onStatusChange === 'function') {
      currentEntry?.statusListeners?.delete(onStatusChange);
    }

    currentEntry.refCount = Math.max(0, currentEntry?.refCount - 1);

    // Only remove the channel when no subscribers remain
    if (currentEntry?.refCount === 0) {
      supabase?.removeChannel(currentEntry?.channel);
      pool?.delete(key);
    }
  };
}

/**
 * Convenience wrapper: subscribe with a single-payload callback (non-batched).
 * Each debounce flush calls callback once per payload in the batch.
 *
 * @param {object} options - Same as subscribePooled, but callback receives a single payload
 * @returns {function} unsubscribe
 */
export function subscribePooledSingle({
  channelName,
  listeners,
  callback,
  debounceMs = 300,
  onStatusChange,
}) {
  return subscribePooled({
    channelName,
    listeners,
    debounceMs,
    onStatusChange,
    callback: (batch) => {
      // Deliver the last payload in the batch (most recent change wins)
      if (batch?.length > 0) {
        callback(batch?.[batch?.length - 1]);
      }
    },
  });
}

/**
 * Force-flush all pending debounce timers across the entire pool.
 * Useful for cleanup in test environments or on app unmount.
 */
export function flushAll() {
  pool?.forEach((entry, key) => {
    entry?.subscribers?.forEach((sub, sid) => {
      if (sub?.timerId !== null) {
        clearTimeout(sub?.timerId);
        flushBatch(entry, sid);
      }
    });
  });
}

/**
 * Return a snapshot of the current pool state (for debugging).
 */
export function getPoolStats() {
  const stats = {};
  pool?.forEach((entry, key) => {
    stats[key] = {
      refCount: entry?.refCount,
      status: entry?.status,
      subscriberCount: entry?.subscribers?.size,
    };
  });
  return stats;
}

export default { subscribePooled, subscribePooledSingle, flushAll, getPoolStats };
